// dynamicFieldSync.ts
import type { Editor } from '@tiptap/core';

/**
 * Reading and writing dynamic (user-created) fields inside the live TipTap doc.
 *
 * Deliberately a smaller, parallel copy of `chainFieldSync.ts`'s shape rather than a
 * generalisation of it: `chainField` is a fixed, developer-registered set with units,
 * formulas and a dependency graph (`measurementChain.ts`) that must stay exactly as it
 * is (17 Sep decision). Dynamic fields carry none of that — a value here is just text
 * that stays in sync across every span sharing its key, nothing more — so this file
 * only needs the plain "find every occurrence, rewrite them all" mechanics, not the
 * warning/dependency machinery chainFieldSync.ts also carries.
 */

type DynamicFieldOccurrence = {
  key: string;
  from: number;
  to: number;
  text: string;
};

/** The first class on the mark is the field's key, matching `classList[0]` in the DOM. */
const keyFromMarkClass = (className: unknown): string | null => {
  if (typeof className !== 'string') {
    return null;
  }
  const first = className.trim().split(/\s+/)[0];
  return first || null;
};

/** Every dynamic field currently in the document, in document order. */
export function findDynamicFieldOccurrences(editor: Editor): DynamicFieldOccurrence[] {
  const found: DynamicFieldOccurrence[] = [];
  if (!editor) {
    return found;
  }

  editor.state.doc.descendants((node, pos) => {
    if (!node.isText) {
      return;
    }
    const mark = node.marks.find(m => m.type.name === 'dynamicField');
    if (!mark) {
      return;
    }
    const key = keyFromMarkClass(mark.attrs.class);
    if (!key) {
      return;
    }
    found.push({ key, from: pos, to: pos + node.nodeSize, text: node.text || '' });
  });

  return found;
}

/**
 * Rewrite every occurrence of `key` to `newValue`, in one transaction. Mirrors
 * `applyChainUpdates`'s back-to-front replacement so an earlier occurrence's
 * position is never invalidated by rewriting a later one first.
 *
 * Returns how many occurrences were rewritten. An empty `newValue` is refused,
 * same reasoning as chainField: an empty span collapses and the field is lost
 * from the report for good, with nothing left to click back open.
 */
export function applyDynamicFieldUpdate(editor: Editor, key: string, newValue: string): number {
  if (!editor || !key || !newValue) {
    return 0;
  }

  const occurrences = findDynamicFieldOccurrences(editor).filter(o => o.key === key);
  if (occurrences.length === 0) {
    return 0;
  }

  const { state } = editor;
  const { tr } = state;
  const markType = state.schema.marks.dynamicField;
  if (!markType) {
    return 0;
  }

  let rewritten = 0;

  for (let i = occurrences.length - 1; i >= 0; i -= 1) {
    const occurrence = occurrences[i];
    if (occurrence.text === newValue) {
      continue; // no change — don't dirty the document for nothing
    }
    const node = state.doc.nodeAt(occurrence.from);
    if (!node || !node.isText) {
      continue;
    }
    tr.replaceWith(occurrence.from, occurrence.to, state.schema.text(newValue, node.marks));
    rewritten += 1;
  }

  if (rewritten > 0) {
    editor.view.dispatch(tr);
  }

  return rewritten;
}

/** What Insert Field lets a clinic user set for a field's definition, edited later by the same dialog. */
export interface DynamicFieldMeta {
  label: string;
  fieldType: 'text' | 'dropdown' | 'number';
  options?: string[];
  unit?: string;
  srConcept?: string;
}

/**
 * Rewrite every occurrence of `key`'s DEFINITION (label/type/options/unit/SR-binding),
 * in one transaction — the template-editor counterpart to `applyDynamicFieldUpdate`,
 * which rewrites a field's VALUE on a report. The key itself never changes here: it is
 * what every occurrence's sync depends on, so renaming it would orphan every existing
 * copy instead of updating them together.
 *
 * An occurrence still showing its own unfilled placeholder ("[Old Label]") has that
 * placeholder text rewritten to match the new label, so it never drifts out of sync
 * with the definition that produced it. A filled-in occurrence's actual value is left
 * exactly as it is — editing the definition must never discard data someone already
 * entered.
 */
export function applyDynamicFieldMetaUpdate(editor: Editor, key: string, meta: DynamicFieldMeta): number {
  if (!editor || !key) {
    return 0;
  }

  const { state } = editor;
  const markType = state.schema.marks.dynamicField;
  if (!markType) {
    return 0;
  }

  const occurrences = findDynamicFieldOccurrences(editor).filter(o => o.key === key);
  if (occurrences.length === 0) {
    return 0;
  }

  const nextAttrs: Record<string, unknown> = {
    class: key,
    title: meta.label,
    'data-field-type': meta.fieldType,
    'data-options':
      meta.fieldType === 'dropdown' && meta.options && meta.options.length > 0
        ? JSON.stringify(meta.options)
        : null,
    'data-unit': meta.fieldType === 'number' && meta.unit ? meta.unit : null,
    'data-sr-concept': meta.srConcept && meta.srConcept.trim() ? meta.srConcept.trim() : null,
  };

  const { tr } = state;
  let updated = 0;

  for (let i = occurrences.length - 1; i >= 0; i -= 1) {
    const occurrence = occurrences[i];
    const node = state.doc.nodeAt(occurrence.from);
    if (!node || !node.isText) {
      continue;
    }
    const currentMark = node.marks.find(m => m.type.name === 'dynamicField');
    const previousLabel = typeof currentMark?.attrs.title === 'string' ? currentMark.attrs.title : key;
    const isUnfilledPlaceholder = occurrence.text === `[${previousLabel}]`;
    const nextMark = markType.create(nextAttrs);

    if (isUnfilledPlaceholder && previousLabel !== meta.label) {
      tr.replaceWith(occurrence.from, occurrence.to, state.schema.text(`[${meta.label}]`, [nextMark]));
    } else {
      tr.removeMark(occurrence.from, occurrence.to, markType);
      tr.addMark(occurrence.from, occurrence.to, nextMark);
    }
    updated += 1;
  }

  if (updated > 0) {
    editor.view.dispatch(tr);
  }

  return updated;
}

/**
 * Which dynamic field did the clinician just retype, comparing every occurrence
 * against the last snapshot? Same shape as `findEditedChainValue` — looks at every
 * occurrence (not just the first) so an edit to a later copy is seen too, and
 * returns null when several fields moved at once (paste, undo, template reload),
 * since there is no single source to propagate from in that case.
 */
export function findEditedDynamicFieldValue(
  editor: Editor,
  previous: Record<string, string>
): { key: string; value: string } | null {
  if (!editor || !previous) {
    return null;
  }

  const changed = new Map<string, string>();
  findDynamicFieldOccurrences(editor).forEach(({ key, text }) => {
    if (!(key in previous)) {
      return; // newly marked field — nothing to compare against yet
    }
    if (text !== previous[key] && text.trim()) {
      changed.set(key, text);
    }
  });

  if (changed.size !== 1) {
    return null;
  }
  const [key, value] = [...changed.entries()][0];
  return { key, value };
}

/** Canonical `key -> value` snapshot for change detection; last occurrence wins on disagreement. */
export function collectDynamicFieldSnapshot(editor: Editor): Record<string, string> {
  const values: Record<string, string> = {};
  findDynamicFieldOccurrences(editor).forEach(({ key, text }) => {
    values[key] = text;
  });
  return values;
}
